// frontend/src/pages/LivePredictions/LivePredictionPanel.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Bell, BellOff, ChevronDown, ChevronUp, Minus, Plus, Lock, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import GlassCard from "../MatchList/components/GlassCard";
import { colors } from "../MatchList/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHECKPOINTS = [5, 10, 15, 20];

const POINTS_META = {
  5: { label: "EXACT", color: colors.accent },
  3: { label: "CLOSE", color: "#facc15" },
  1: { label: "NEAR", color: "#fb923c" },
  0: { label: "MISS", color: "rgba(255,255,255,0.35)" },
};

function getCurrentCheckpoint(currentOvers) {
  for (const cp of CHECKPOINTS) {
    if (currentOvers < cp) return cp;
  }
  return null;
}

function getInningsAndOvers(match) {
  const teamBScore = match.team_b_score;
  if (teamBScore != null && teamBScore > 0) {
    return { innings: 2, overs: match.team_b_overs || 0 };
  }
  return { innings: 1, overs: match.team_a_overs || 0 };
}

export default function LivePredictionPanel({ match, user }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [sliderValue, setSliderValue] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [togglingMute, setTogglingMute] = useState(false);

  const fetchPredictions = useCallback(async () => {
    try {
      const fetchFn = auth?.authFetch || fetch;
      const res = await fetchFn(`${BACKEND_URL}/api/matches/${match.id}/live-predictions/me`);
      if (res.ok) {
        const data = await res.json();
        setPredictions(data.predictions || []);
      }
    } catch (_) {
      /* ignore */
    }
  }, [match.id, auth]);

  const fetchSettings = useCallback(async () => {
    try {
      const fetchFn = auth?.authFetch || fetch;
      const res = await fetchFn(`${BACKEND_URL}/api/users/me/settings`);
      if (res.ok) {
        const data = await res.json();
        setMuted(!!data.live_notification_muted);
      }
    } catch (_) {
      /* ignore */
    }
  }, [auth]);

  useEffect(() => {
    fetchPredictions();
    fetchSettings();
    const interval = setInterval(fetchPredictions, 5000);
    return () => clearInterval(interval);
  }, [fetchPredictions, fetchSettings]);

  const { innings, overs } = getInningsAndOvers(match);
  const nextCheckpoint = getCurrentCheckpoint(overs);
  const alreadyPredicted = (cp, inn) =>
    predictions.some((p) => p.checkpoint_over === cp && p.innings === inn);

  const currentScore = innings === 1 ? match.team_a_score || 0 : match.team_b_score || 0;
  const battingTeam = innings === 1 ? match.team_a : match.team_b;
  const canPredict = nextCheckpoint && !alreadyPredicted(nextCheckpoint, innings);

  // Seed slider with a reasonable projection ONCE per checkpoint window
  // (don't stomp on user input while polling refreshes overs)
  const seededKey = `${innings}-${nextCheckpoint}`;
  const [seeded, setSeeded] = useState(null);
  useEffect(() => {
    if (!canPredict) return;
    if (seeded === seededKey) return;
    const runsPerOver = overs > 0 ? currentScore / overs : 7;
    const estimate = Math.round(currentScore + runsPerOver * (nextCheckpoint - overs));
    setSliderValue(Math.max(0, estimate));
    setSeeded(seededKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededKey, canPredict]);

  const handleSubmit = async () => {
    if (!nextCheckpoint) return;
    setSubmitting(true);
    try {
      const fetchFn = auth?.authFetch || fetch;
      const res = await fetchFn(`${BACKEND_URL}/api/matches/${match.id}/live-predictions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checkpoint_over: nextCheckpoint,
          innings,
          predicted_score: sliderValue,
        }),
      });
      if (res.ok) {
        toast.success(`Prediction locked for Over ${nextCheckpoint}`);
        try {
          localStorage.setItem("livePredictionsInfoCardSeen", "1");
        } catch (_) {
          /* ignore */
        }
        fetchPredictions();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit");
      }
    } catch (_) {
      toast.error("Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMute = async () => {
    setTogglingMute(true);
    try {
      const fetchFn = auth?.authFetch || fetch;
      const res = await fetchFn(`${BACKEND_URL}/api/users/me/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ live_notification_muted: !muted }),
      });
      if (res.ok) {
        setMuted(!muted);
      }
    } catch (_) {
      toast.error("Failed to update settings");
    } finally {
      setTogglingMute(false);
    }
  };

  const scoredPredictions = predictions.filter((p) => p.points_earned !== null);
  const totalPoints = scoredPredictions.reduce((s, p) => s + (p.points_earned || 0), 0);

  return (
    <GlassCard padding="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <Zap className="w-3.5 h-3.5" style={{ color: colors.accent }} />
            <span
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color: colors.accent }}
            >
              Live Predictions
            </span>
          </div>
          <p className="text-[11px] text-white/40">
            Score at each checkpoint over
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            disabled={togglingMute}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            title={muted ? "Unmute" : "Mute"}
            aria-label={muted ? "Unmute notifications" : "Mute notifications"}
          >
            {muted ? (
              <BellOff className="w-4 h-4 text-white/40" />
            ) : (
              <Bell className="w-4 h-4" style={{ color: colors.accent }} />
            )}
          </button>
          {scoredPredictions.length > 0 && (
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
              style={{
                background: "rgba(1,255,154,0.08)",
                border: "1px solid rgba(1,255,154,0.2)",
              }}
            >
              <span className="text-[10px] font-medium text-white/60">You</span>
              <span
                className="text-sm font-bold tabular-nums"
                style={{ color: colors.accent }}
              >
                {totalPoints}
              </span>
              <span className="text-[10px] text-white/40">pts</span>
            </div>
          )}
        </div>
      </div>

      {/* Checkpoint prompt */}
      {canPredict ? (
        <motion.div
          key={`${innings}-${nextCheckpoint}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-2xl mb-4"
          style={{
            background:
              "linear-gradient(180deg, rgba(1,255,154,0.08) 0%, rgba(1,255,154,0.02) 100%)",
            border: "1px solid rgba(1,255,154,0.22)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-widest text-white/40">
                Innings {innings} · {battingTeam}
              </p>
              <p className="text-[15px] font-semibold text-white mt-0.5">
                Score at end of Over {nextCheckpoint}?
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-medium uppercase tracking-wider text-white/40">Now</p>
              <p className="text-lg font-bold text-white tabular-nums">
                {currentScore}
                <span className="text-xs text-white/30">/{overs.toFixed ? overs.toFixed(1) : overs}</span>
              </p>
            </div>
          </div>

          {/* Giant value + step buttons */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <button
              className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${colors.cardBorder}`,
              }}
              onClick={() => setSliderValue((v) => Math.max(0, v - 1))}
              aria-label="Decrease"
            >
              <Minus className="w-4 h-4 text-white/80" />
            </button>

            <div className="flex-1 text-center">
              <motion.p
                key={sliderValue}
                initial={{ scale: 0.95, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="text-[52px] font-bold leading-none tabular-nums"
                style={{
                  background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {sliderValue}
              </motion.p>
              <p className="text-[10px] text-white/40 mt-1 uppercase tracking-wider">runs</p>
            </div>

            <button
              className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${colors.cardBorder}`,
              }}
              onClick={() => setSliderValue((v) => v + 1)}
              aria-label="Increase"
            >
              <Plus className="w-4 h-4 text-white/80" />
            </button>
          </div>

          <Slider
            value={[sliderValue]}
            onValueChange={(v) => setSliderValue(v[0])}
            min={Math.max(0, currentScore)}
            max={Math.max(currentScore + 120, 300)}
            step={1}
            className="mb-4"
          />

          {/* Points scoring hint */}
          <div className="flex items-center justify-between mb-4 text-[10px] text-white/40">
            <div className="flex items-center gap-1">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: colors.accent }}
              />
              <span>±0 = 5 pts</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
              <span>±2 = 3 pts</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
              <span>±5 = 1 pt</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.99] disabled:opacity-60"
            style={{
              background: "linear-gradient(90deg, #01ff9a, #acffe5)",
              color: "#0f0f0f",
            }}
          >
            <Lock className="w-4 h-4" />
            {submitting ? "Locking…" : `Lock for Over ${nextCheckpoint}`}
          </button>
        </motion.div>
      ) : nextCheckpoint && alreadyPredicted(nextCheckpoint, innings) ? (
        <div
          className="p-4 rounded-2xl mb-4 flex items-center gap-3"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${colors.cardBorder}`,
          }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(1,255,154,0.1)" }}
          >
            <Lock className="w-4 h-4" style={{ color: colors.accent }} />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-white">
              Locked for Over {nextCheckpoint}
            </p>
            <p className="text-[11px] text-white/40 mt-0.5">
              Waiting for the over to finish…
            </p>
          </div>
        </div>
      ) : (
        <div
          className="p-4 rounded-2xl mb-4 text-center"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${colors.cardBorder}`,
          }}
        >
          <p className="text-sm text-white/60">
            {match.status === "completed"
              ? "Match completed — check the live leaderboard."
              : overs >= 20
              ? "All checkpoints done for this innings."
              : "Next checkpoint opens soon."}
          </p>
        </div>
      )}

      {/* Past checkpoints */}
      {scoredPredictions.length > 0 && (
        <div>
          <button
            onClick={() => setShowPrevious(!showPrevious)}
            className="flex items-center justify-between w-full py-2 text-[11px] uppercase tracking-widest text-white/40 hover:text-white/60 transition-colors"
          >
            <span>Past checkpoints · {scoredPredictions.length}</span>
            {showPrevious ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>

          <AnimatePresence>
            {showPrevious && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-2 pt-2">
                  {scoredPredictions.map((p) => {
                    const meta = POINTS_META[p.points_earned] || POINTS_META[0];
                    return (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-3 rounded-xl"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: `1px solid ${colors.cardBorder}`,
                        }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                            style={{
                              background: `${meta.color}14`,
                              color: meta.color,
                            }}
                          >
                            {p.checkpoint_over}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[11px] text-white/40">
                              Inn {p.innings} · Over {p.checkpoint_over}
                            </p>
                            <p className="text-xs text-white mt-0.5">
                              You: <span className="font-semibold tabular-nums">{p.predicted_score}</span>
                              <span className="text-white/30 mx-1.5">·</span>
                              Actual: <span className="font-semibold tabular-nums">{p.actual_score}</span>
                            </p>
                          </div>
                        </div>
                        <div
                          className="px-2.5 py-1 rounded-lg text-[11px] font-bold tracking-wide flex-shrink-0"
                          style={{
                            background: `${meta.color}14`,
                            color: meta.color,
                            border: `1px solid ${meta.color}33`,
                          }}
                        >
                          +{p.points_earned} {meta.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </GlassCard>
  );
}
